import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Outlet } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { CsrAPI } from "../apis/CsrAPI";
import { useSession } from "./SessionContext";
import { dashboardPermission } from "../utils/permissions";

const PermissionsContext = createContext(null);

export const PermissionsProvider = () => {
  const [permissions, setPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const { isAuthenticated, isLoading } = useAuth0();
  const { sessionReady } = useSession();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      setPermissions({});
      setErrMsg("");
      setPermissionsLoading(false);
      return;
    }

    // /me/permissions needs the session the login call establishes, so it waits
    // for sessionReady the same way the feature flags do.
    if (!sessionReady) {
      return;
    }

    let cancelled = false;
    setPermissionsLoading(true);
    CsrAPI.getPermissions()
      .then(data => {
        if (cancelled) return;
        setPermissions(data || {});
        setErrMsg("");
      })
      .catch(() => {
        if (cancelled) return;
        // Failing closed: with no permission map every gated control stays
        // disabled rather than letting through an action the API would reject.
        setPermissions({});
        setErrMsg("Failed to get user permissions");
      })
      .finally(() => {
        if (!cancelled) {
          setPermissionsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, sessionReady]);

  // Only an explicit true grants access; a missing or false key denies it.
  const hasPermission = useCallback(
    key => permissions?.[key] === true,
    [permissions]
  );

  const canOnDashboard = useCallback(
    (studyType, action) =>
      hasPermission(dashboardPermission(studyType, action)),
    [hasPermission]
  );

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        permissionsLoading,
        errMsg,
        hasPermission,
        canOnDashboard,
      }}>
      <Outlet />
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);

  if (!context) {
    throw new Error("Please use PermissionsProvider in parent component");
  }

  return context;
};

export default PermissionsProvider;
